import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { QuestionFill } from "../components/QuestionDesign";
import {
  eventAudienceLabel,
  eventDescription,
  eventHiddenFromFamilies,
  eventOpenToStudent,
} from "../domain/data";
import { useEvents } from "../state/events";
import { useLocale } from "../state/i18n";
import { useMail } from "../state/MailProvider";
import {
  displayAnswer,
  missingRequiredQuestion,
  questionLabel,
} from "../domain/questions";
import { useStudents } from "../state/students";
import { ErrorFlash } from "../components/ErrorFlash";
import { waitlistPlace } from "../domain/registrations";
import {
  ChildSwitcher,
  EventDescriptionBlock,
  EventSeatSummary,
  PrototypeBanner,
  StudentNav,
} from "../components/ui";

export function StudentEvent() {
  const { studentId = "mike", eventId = "fall-activity-day" } = useParams();
  const { getStudent } = useStudents();
  const student = getStudent(studentId);
  const {
    getEvent,
    registrationForStudent,
    registrationsFor,
    registerStudent,
    cancelRegistration,
    countsFor,
  } = useEvents();
  const event = getEvent(eventId);
  const { locale, t } = useLocale();
  const { eventMailFor } = useMail();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [error, setError] = useState("");

  if (!student || !event || eventHiddenFromFamilies(event)) {
    return (
      <div className="app">
        <PrototypeBanner />
        <main className="shell">
          <p>{t("studentEvent.notFound")}</p>
          <Link to="/">{t("student.back")}</Link>
        </main>
      </div>
    );
  }

  const liftEvent = event;
  const currentStudent = student;
  const allowed = eventOpenToStudent(liftEvent, currentStudent);
  const registration = registrationForStudent(liftEvent.id, currentStudent.id);
  const counts = countsFor(liftEvent.id);
  const registered = registration?.status === "registered";
  const waitlisted = registration?.status === "waitlisted";
  const place = waitlisted
    ? waitlistPlace(registrationsFor(liftEvent.id), currentStudent.id)
    : null;
  const confirmation =
    registered || waitlisted
      ? eventMailFor(currentStudent.id, liftEvent.id)
      : undefined;
  const questions = liftEvent.questions ?? [];

  function onRegister(formEvent: FormEvent) {
    formEvent.preventDefault();
    if (!allowed) return;
    const missing = missingRequiredQuestion(questions, answers);
    if (missing) {
      setError(t("register.errorQuestion", { label: questionLabel(missing, locale) }));
      return;
    }
    setError("");
    registerStudent(liftEvent.id, currentStudent.id, answers);
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <StudentNav studentId={currentStudent.id} />
        <ChildSwitcher studentId={currentStudent.id} />
        <p className="meta">{t("studentEvent.meta")}</p>
        <h1>{event.name}</h1>
        <p className="lede">
          {event.dateLabel} · {event.timeLabel}
          <br />
          {event.location}
        </p>

        <EventSeatSummary
          registered={counts.registered}
          capacity={event.capacity}
          remaining={counts.remaining}
          waitlisted={counts.waitlisted}
          waitlistEnabled={event.waitlistEnabled}
          audience={eventAudienceLabel(event)}
        />

        <EventDescriptionBlock
          label={t("events.about")}
          text={eventDescription(event, locale)}
        />

        {allowed || registration ? (
          <>
            <h2>{t("studentEvent.registeringAs")}</h2>
            <div className="wire dashed">
              <strong>
                {currentStudent.given} {currentStudent.family}
              </strong>
              <div className="anno">
                {currentStudent.school} · {t("family.grade", { grade: currentStudent.grade })}{" "}
                · {t("studentEvent.guardian", { name: currentStudent.guardianName })}
              </div>
            </div>

            {registered && (
              <div className="flash flash--success" role="status">
                {t("studentEvent.registeredFlash")}
              </div>
            )}
            {waitlisted && (
              <div className="flash flash--warning" role="status">
                {place === 1
                  ? t("studentEvent.waitlistedNextUp")
                  : t("studentEvent.waitlistedFlash", { place: place ?? "—" })}
                {place !== 1 ? (
                  <div className="flash__detail">{t("studentEvent.waitlistedMove")}</div>
                ) : null}
              </div>
            )}
            {confirmation ? (
              <div className="wire auth-mail" style={{ marginTop: "1rem" }}>
                <div className="meta">{t("studentEvent.mailMeta")}</div>
                <strong>{confirmation.subject}</strong>
                <div className="anno">
                  {t("studentEvent.mailNote", { email: confirmation.to.join(", ") })}
                </div>
                <p className="mail-body">{confirmation.body}</p>
              </div>
            ) : null}

            {registration && questions.length > 0 ? (
              <div className="wire" style={{ marginTop: "1rem" }}>
                <div className="meta">{t("studentEvent.answers")}</div>
                {questions.map((item) => (
                  <div className="field" key={item.id} style={{ marginTop: "0.75rem" }}>
                    <label>{questionLabel(item, locale)}</label>
                    <div className="value">
                      {displayAnswer(item, registration.answers?.[item.id], locale)}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            <form className="stack" onSubmit={onRegister} style={{ marginTop: "1rem" }}>
              {!registration && questions.length > 0 ? (
                <>
                  <h2 style={{ marginTop: 0 }}>{t("studentEvent.thisTrip")}</h2>
                  {questions.map((item) => (
                    <div className="field" key={item.id}>
                      <label htmlFor={item.id}>
                        {questionLabel(item, locale)}
                        {item.required ? "" : ` ${t("register.optional")}`}
                      </label>
                      <QuestionFill
                        question={item}
                        value={answers[item.id] ?? ""}
                        onChange={(value) =>
                          setAnswers((current) => ({
                            ...current,
                            [item.id]: value,
                          }))
                        }
                      />
                    </div>
                  ))}
                </>
              ) : null}

              {error ? <ErrorFlash key={error} message={error} /> : null}

              {!registration && counts.remaining <= 0 ? (
                <p className="lede" style={{ marginBottom: 0 }}>
                  {event.waitlistEnabled
                    ? t("studentEvent.waitlistExplain")
                    : t("studentEvent.fullBody")}
                </p>
              ) : null}

              <div className="actions">
                <button type="submit" disabled={Boolean(registration)}>
                  {registered
                    ? t("student.registered")
                    : waitlisted
                      ? place === 1
                        ? t("studentEvents.waitlistNext")
                        : t("studentEvents.waitlistPlace", { place: place ?? "—" })
                      : counts.remaining > 0
                        ? t("studentEvent.register")
                        : event.waitlistEnabled
                          ? t("studentEvent.joinWaitlist")
                          : t("studentEvent.full")}
                </button>
                {registration ? (
                  <button
                    type="button"
                    className="ghost"
                    onClick={() =>
                      cancelRegistration(liftEvent.id, currentStudent.id)
                    }
                  >
                    {waitlisted
                      ? t("studentEvent.leaveWaitlist")
                      : t("studentEvent.cancel")}
                  </button>
                ) : null}
                {registered && (
                  <Link className="btn" to={`/student/${currentStudent.id}/events`}>
                    {t("studentEvent.view")}
                  </Link>
                )}
              </div>
            </form>
          </>
        ) : (
          <div className="actions">
            <Link className="btn ghost" to={`/student/${currentStudent.id}`}>
              {t("student.backToEvents")}
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
